import { useEffect, useRef } from 'react';
import { api } from '../lib/axios';
import { useFirebaseAuth } from '../contexts/FirebaseAuthContext';

const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

const sendHeartbeat = async () => {
  try {
    await api.post('/api/auth/heartbeat');
  } catch {
    // Silent fail — no toast/alert needed
  }
};

export function useHeartbeat() {
  const { user, loading } = useFirebaseAuth();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (loading || !user) return;

    const startInterval = () => {
      intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        sendHeartbeat();
        startInterval();
      }
    };

    // Send immediately on mount, then start interval
    sendHeartbeat();
    startInterval();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, loading]);
}
